import { Link } from "react-router-dom";
import { ScrollVideo } from "@/components/ScrollVideo";
import { Reveal } from "@/components/Reveal";
import { Glyph } from "@/components/Glyph";
import { ProductImage } from "@/components/ProductImage";
import { useProducts } from "@/lib/useProducts";
import { useSeo } from "@/lib/seo";
import { useLang, lp, loc } from "@/lib/i18n";
import { VIDEO, POSTER } from "@/assets";

const T = {
  fr: {
    seoTitle: "Crisopea — la voie du feu · Maison Partita",
    seoDesc:
      "Sept eaux de parfum, une par métal de l'alchimie, chacune sous sa planète et son glyphe. Série limitée numérotée, 50 ml.",
    kicker: "La voie du feu",
    intro1:
      "Sept eaux de parfum, une par métal de l'alchimie, chacune sous sa planète et son glyphe. La collection suit l'ordre du Grand Œuvre, du plomb de Saturne jusqu'à l'or du Soleil — la transmutation lue comme une progression, du métal le plus sourd au métal le plus lumineux.",
    intro2:
      "Le principe fondateur : le glyphe n'est pas un décor, il est tracé dans le métal réel de la référence. On ne lit pas le nom du parfum, on reconnaît son astre.",
    format: "Eau de parfum concentrée · 50 ml · Série limitée numérotée.",
    film: "Film · La voie du feu",
    theRefs: "Les sept métaux",
    days: "☉ Dimanche — Or · ☽ Lundi — Argent · ♂ Mardi — Fer · ☿ Mercredi — Mercure · ♃ Jeudi — Étain · ♀ Vendredi — Cuivre · ♄ Samedi — Plomb",
    soldOut: "Épuisé",
    note:
      "La plupart des références sont épuisées et leur retour n'est pas garanti. Montrer la collection entière est le principe même de la maison.",
    alt: (roman, metal, planet) => `Étui CRISOPEA ${roman} — ${metal}, ${planet}`,
  },
  en: {
    seoTitle: "Crisopea — the way of fire · Maison Partita",
    seoDesc:
      "Seven eaux de parfum, one per alchemical metal, each under its planet and its glyph. Numbered limited series, 50 ml.",
    kicker: "The way of fire",
    intro1:
      "Seven eaux de parfum, one per alchemical metal, each under its planet and its glyph. The collection follows the order of the Great Work, from the lead of Saturn to the gold of the Sun — transmutation read as a progression, from the dullest metal to the most luminous.",
    intro2:
      "The founding principle: the glyph is not decoration, it is drawn in the actual metal of the reference. You do not read the name of the perfume, you recognise its star.",
    format: "Concentrated eau de parfum · 50 ml · Numbered limited series.",
    film: "Film · The way of fire",
    theRefs: "The seven metals",
    days: "☉ Sunday — Gold · ☽ Monday — Silver · ♂ Tuesday — Iron · ☿ Wednesday — Mercury · ♃ Thursday — Tin · ♀ Friday — Copper · ♄ Saturday — Lead",
    soldOut: "Sold out",
    note:
      "Most references are sold out and their return is not guaranteed. Showing the whole collection is the very principle of the house.",
    alt: (roman, metal, planet) => `CRISOPEA ${roman} case — ${metal}, ${planet}`,
  },
};

export default function Crisopea() {
  const lang = useLang();
  const t = T[lang];
  useSeo(t.seoTitle, t.seoDesc);
  const { products } = useProducts("crisopea");

  return (
    <div>
      <section className="max-w-3xl mx-auto px-5 sm:px-10 pt-28 pb-24">
        <Reveal>
          <p className="text-[13px] tracking-[0.34em] uppercase" style={{ color: "#C9A86A" }}>{t.kicker}</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-[0.12em] uppercase text-zinc-50">Crisopea</h1>
          <p className="mt-10 text-[19px] leading-[1.8] text-zinc-300">{t.intro1}</p>
          <p className="mt-8 text-[19px] leading-[1.8] text-zinc-300">{t.intro2}</p>
          <p className="mt-6 text-[16px] text-zinc-500">{t.format}</p>
        </Reveal>
      </section>

      <ScrollVideo src={VIDEO.crisopea} poster={POSTER.crisopea} heightVh={180}>
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 sm:px-10 flex items-end pb-16">
          <p className="text-[13px] tracking-[0.34em] uppercase" style={{ color: "#C9A86A" }}>{t.film}</p>
        </div>
      </ScrollVideo>

      <div
        className="mt-2 py-4 text-center text-[11px] sm:text-[12px] tracking-[0.24em] uppercase border-y"
        style={{ color: "rgba(201,168,106,0.75)", borderColor: "rgba(201,168,106,0.18)" }}
      >
        {t.days}
      </div>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-24 pb-32">
        <Reveal>
          <div className="flex items-baseline justify-between border-b pb-4" style={{ borderColor: "rgba(201,168,106,0.18)" }}>
            <h2 className="text-2xl tracking-[0.24em] uppercase text-zinc-100">{t.theRefs}</h2>
            <span className="text-[13px] tracking-[0.16em] uppercase text-zinc-600">I — VII</span>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-x-8 gap-y-16 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.08}>
              <Link
                to={lp(lang, `/crisopea/${p.slug}`)}
                data-testid={`crisopea-${p.slug}`}
                className="group block"
              >
                <div className="relative overflow-hidden border" style={{ background: "#111", borderColor: "rgba(201,168,106,0.16)" }}>
                  <ProductImage
                    maison="crisopea"
                    reference={p.reference}
                    suffix="etui"
                    alt={t.alt(p.roman, loc(p, lang, "metal"), loc(p, lang, "planet"))}
                    className={`transition-all duration-[1200ms] ease-out group-hover:scale-[1.03] ${p.available ? "opacity-100" : "opacity-70 group-hover:opacity-90"}`}
                  />
                  <span className="absolute top-4 right-4">
                    <Glyph glyph={p.glyph} color={p.color} size={40} faint={!p.available} />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-[15px] tracking-[0.2em] uppercase text-zinc-100">CRISOPEA {p.roman}</h3>
                  <p className="mt-1 text-[13px] tracking-[0.08em] text-zinc-500">
                    {loc(p, lang, "metal")} · {loc(p, lang, "planet")}
                  </p>
                  {p.available ? (
                    <p className="mt-2" style={{ color: "#C9A86A" }}>{p.price} €</p>
                  ) : (
                    <p className="mt-2 text-[12px] tracking-[0.2em] uppercase text-zinc-600">{t.soldOut}</p>
                  )}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="mt-20 max-w-2xl text-[15px] leading-[1.8] text-zinc-500">{t.note}</p>
        </Reveal>
      </section>
    </div>
  );
}
